import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminEducator } from '../types';
import { portalApi } from '../utils/api';
import FacultyPortalPage from './FacultyPortalPage';

function statusChipColor(status: AdminEducator['approval_status']) {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
}

export default function AdminPortalPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'admin' | 'educator'>('admin');
  const [addEducatorDialogOpen, setAddEducatorDialogOpen] = useState(false);
  const [newEducatorFirstName, setNewEducatorFirstName] = useState('');
  const [newEducatorLastName, setNewEducatorLastName] = useState('');
  const [newEducatorEmail, setNewEducatorEmail] = useState('');
  const [newEducatorPassword, setNewEducatorPassword] = useState('');
  const [createEducatorError, setCreateEducatorError] = useState<string | null>(null);
  const [createEducatorSuccess, setCreateEducatorSuccess] = useState<string | null>(null);

  const {
    data: educators = [],
    isLoading,
    error,
  } = useQuery<AdminEducator[]>({
    queryKey: ['portal', 'admin', 'educators'],
    queryFn: portalApi.getAdminEducators,
  });

  const approveMutation = useMutation({
    mutationFn: (educatorUserId: string) => portalApi.approveAdminEducator(educatorUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'admin', 'educators'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (educatorUserId: string) => portalApi.removeAdminEducator(educatorUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'admin', 'educators'] });
    },
  });

  const createEducatorMutation = useMutation({
    mutationFn: (payload: { first_name: string; last_name: string; email: string; password: string }) =>
      portalApi.createAdminEducator(payload),
    onSuccess: () => {
      setCreateEducatorError(null);
      setCreateEducatorSuccess('Educator account created and approved.');
      setAddEducatorDialogOpen(false);
      setNewEducatorFirstName('');
      setNewEducatorLastName('');
      setNewEducatorEmail('');
      setNewEducatorPassword('');
      queryClient.invalidateQueries({ queryKey: ['portal', 'admin', 'educators'] });
    },
    onError: (mutationError) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Unable to create educator account';
      setCreateEducatorError(message);
    },
  });

  const handleCreateEducator = () => {
    setCreateEducatorError(null);
    setCreateEducatorSuccess(null);
    createEducatorMutation.mutate({
      first_name: newEducatorFirstName,
      last_name: newEducatorLastName,
      email: newEducatorEmail,
      password: newEducatorPassword,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Typography variant="h4" fontWeight={700}>Admin Portal</Typography>
          <Typography color="text.secondary">Manage educator approvals and access</Typography>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, value: 'admin' | 'educator') => setActiveTab(value)}
          aria-label="Admin portal view tabs"
        >
          <Tab value="admin" label="Admin" />
          <Tab value="educator" label="Educator" />
        </Tabs>

        {error && <Alert severity="error">Unable to load educators</Alert>}

        {activeTab === 'admin' ? (
          <>
            {createEducatorError && <Alert severity="error">{createEducatorError}</Alert>}
            {createEducatorSuccess && <Alert severity="success">{createEducatorSuccess}</Alert>}

            <Card>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6">Educators</Typography>
                  <Button variant="contained" onClick={() => setAddEducatorDialogOpen(true)}>
                    Add Educator
                  </Button>
                </Stack>

                {educators.length === 0 ? (
                  <Typography color="text.secondary">No educators found for your institution.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {educators.map((educator) => (
                      <Card key={educator.user_id} variant="outlined">
                        <CardContent>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                          >
                            <Box>
                              <Typography fontWeight={700}>{`${educator.first_name || ''} ${educator.last_name || ''}`.trim() || educator.email || educator.user_id}</Typography>
                              <Typography color="text.secondary">{educator.email || 'No email'}</Typography>
                              <Typography color="text.secondary">{educator.institution || 'No institution'}</Typography>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                              <Chip
                                label={educator.approval_status}
                                color={statusChipColor(educator.approval_status)}
                                size="small"
                              />
                              <Chip
                                label={educator.is_active ? 'active' : 'inactive'}
                                color={educator.is_active ? 'success' : 'default'}
                                size="small"
                              />
                              <Button
                                variant="contained"
                                size="small"
                                disabled={approveMutation.isPending || educator.approval_status === 'approved'}
                                onClick={() => approveMutation.mutate(educator.user_id)}
                              >
                                Approve
                              </Button>
                              <Button
                                color="error"
                                variant="outlined"
                                size="small"
                                disabled={removeMutation.isPending || !educator.is_active}
                                onClick={() => removeMutation.mutate(educator.user_id)}
                              >
                                Remove
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Typography color="text.secondary">
              Educator view shows classes created under your admin account.
            </Typography>
            <FacultyPortalPage />
          </>
        )}
      </Stack>

      <Dialog
        open={addEducatorDialogOpen}
        onClose={() => !createEducatorMutation.isPending && setAddEducatorDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Educator</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First Name"
                value={newEducatorFirstName}
                onChange={(event) => setNewEducatorFirstName(event.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Last Name"
                value={newEducatorLastName}
                onChange={(event) => setNewEducatorLastName(event.target.value)}
                required
                fullWidth
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              value={newEducatorEmail}
              onChange={(event) => setNewEducatorEmail(event.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Temporary Password"
              type="password"
              value={newEducatorPassword}
              onChange={(event) => setNewEducatorPassword(event.target.value)}
              helperText="Minimum 8 characters"
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEducatorDialogOpen(false)} disabled={createEducatorMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={createEducatorMutation.isPending}
            onClick={handleCreateEducator}
          >
            {createEducatorMutation.isPending ? 'Creating...' : 'Create Educator'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
